/**
 * Copy for the create-central-order wizard.
 *
 * Kept apart from `create-order.ts` rather than merged into it: that file held the legacy shared
 * order/claim form's copy, and other screens still read it. The two word the same ideas
 * differently.
 */
export default {
  pageTitle: 'Create new order',
  pageSubtitle: 'Orders',

  steps: {
    details: { label: 'Details', hint: 'Site & delivery' },
    inventory: { label: 'Inventory', hint: 'Add BOQ Items' },
    /**
     * "Payments", not "Confirm": the step sets payment method, schedule, assignee and documentation
     * — it is where the payment details are entered, not a read-through of what came before. The
     * key stays `confirm` so every `data-cy`, form id and namespace is unchanged. Mirrors the claim
     * flow's twin.
     */
    confirm: { label: 'Payments', hint: 'Method, schedule & docs' },
  },

  toast: {
    /** One order request per vendor, so a basket spanning several places more than one. */
    placed: 'Order placed | Orders placed',
    /**
     * A 2xx that carried no orders. Nothing was created, so the user has to be told — otherwise the
     * button simply stops spinning and the step sits there looking ignored.
     */
    notPlaced: {
      title: 'Order not placed',
      content: 'Nothing was created. Please try again.',
    },
  },

  actions: {
    back: 'Back',
    cancel: 'Cancel',
    next: 'Next',
    /**
     * One order request per vendor, so a basket spanning several places more than one order. The
     * count picks the form; it isn't shown — "Place 3 orders" reads as a quantity of a thing rather
     * than the act of placing them.
     */
    /**
     * Context: "Request", not "Place" — pressing this raises the request; the order is only
     * *placed* once it is approved. The button naming the later step overstated what the press
     * does.
     */
    requestOrder: 'Request order | Request orders',
  },

  details: {
    section: {
      title: 'Project, site & delivery date',
      subtitle: "Inventory items are pulled from this site's BOQ",
    },
    project: {
      label: 'Project',
      placeholder: 'Select a project',
      emptyMessage: 'No projects found',
    },
    site: {
      label: 'Site',
      placeholder: 'Select a site',
      emptyMessage: 'No sites with an approved BOQ',
    },
    deliveryDate: {
      label: 'Delivery date',
      placeholder: 'Select a date',
      /** Shown under the field once the site's range is known. */
      hint: 'Site dates: {range}',
    },
    note: {
      sectionTitle: 'Note',
      sectionSubtitle: 'Add an optional note to this order',
      label: 'Note',
      placeholder: 'Add a note to this order',
    },
    siteGuidance: {
      message:
        'Items are ordered against the selected site. To order items not assigned to a specific site, create a site for General or Head Office purchases.',
      action: 'Create site',
    },
  },

  inventory: {
    title: 'Order inventory',
    subtitle:
      "Search the site's BOQ to add items. Quantities over the available BOQ raise a variation.",
    clearAll: 'Clear all',
    /** The running count beside the title, e.g. "2 items across 2 vendors". */
    summary: '{items} across {vendors}',
    itemCount: '1 item | {count} items',
    vendorCount: '1 vendor | {count} vendors',

    /**
     * What the shared inventory components under `components/Inventory` can't say for themselves,
     * because it names the document being built. Everything else they show lives in the neutral
     * `inventory.*` namespace — see `translations/en/inventory.ts`.
     */
    table: {
      empty: {
        title: 'No items added yet',
        description: "Search the site's BOQ above to add the first line to this order.",
      },
      /** The remaining-balance tooltip's row for what this order is taking off the line. */
      selectedOnThisOrder: 'Selected on this order',
    },

    totals: {
      orderTotal: 'Order total (incl VAT)',
      projectedTotal: 'Order total if approved (incl VAT)',
    },

    clearDialog: {
      title: 'Clear the order inventory?',
      body: "Every line you've added will be removed, along with the quantities and rates set against them. The site's BOQ isn't affected, so you can add them again.",
      confirm: 'Clear 1 item | Clear all {count} items',
      cancel: 'Keep them',
    },
  },

  confirm: {
    title: 'Payment details',
    subtitle:
      'One order request per vendor. Set payment, an optional deposit and documentation on each.',

    /**
     * What the shared vendor-payments components under `components/VendorPayments` can't say for
     * themselves, because it names the document being built. Everything else they show lives in the
     * neutral `vendorPayments.*` namespace — see `translations/en/vendor-payments.ts`.
     */
    requestType: { order: 'Order' },

    documentation: {
      title: 'Order documentation',
      infoMessage:
        'These documents are optional at creation. You can also upload them later from the order detail page on an individual order.',
      accordionTitle: 'Key documentation',
      accordionSubtitle:
        'All documents required for the completion of this order and its associated payments.',
      prerequisitesTitle: 'Order prerequisites',
      quoteLabel: 'Quote/Proforma Invoice',
      invoiceLabel: 'Tax Invoice',
      fileHint: 'PDF, JPG or PNG · up to 10MB per file · Multiple files supported',
      done: 'Done',
    },

    emptyTitle: 'Nothing to confirm yet',
    emptyDescription: 'Add items on the Inventory step and the vendors to pay will appear here.',

    table: {
      columns: {
        /** Heads the column holding a deposit's share. Orders raise no retention at creation. */
        deposit: 'Deposit',
      },
      tooltips: {
        method:
          "Sets this order's payment priority. Cash on delivery is paid before the due date, account payments when the vendor account is settled, and a deposit before the delivery date.",
        deposit:
          'Splits the approved order into two payment requests: the deposit, and one for the balance.',
      },
    },
  },

  leaveDialog: {
    title: 'Discard this order?',
    body: "Your progress isn't saved anywhere. Leaving now discards what you've entered and you'll need to start this order again.",
    confirm: 'Discard order',
    cancel: 'Keep editing',
  },

  clearInventoryDialog: {
    title: 'Change the {subject}?',
    body: "The items on this order come from the current site's BOQ, so changing the {subject} removes every line you've added, along with anything set on the Confirm step. You'll need to add the items again against the new site.",
    confirm: 'Change and clear',
    cancel: 'Keep what I have',
    subject: { project: 'project', site: 'site' },
  },

  deliveryDateDialog: {
    title: 'Delivery date is outside the site dates',
    body: "The delivery date you've picked ({date}) falls outside this site's dates of {range}.",
    /**
     * Stops at "when it's approved" deliberately: the API extends the range to cover the approval
     * date, not the delivery date, so naming the resulting dates would promise what it doesn't do.
     */
    note: "You can still use it. The site's dates are extended to cover this order when it's approved, and any linked draws move with them.",
    confirm: 'Use this date',
    cancel: 'Pick another date',
  },
};
